import type { CoverageStatus, Policy, PolicyCoverages } from "@shared/schema";
import {
  hasStandardAutoLiabilityLimit,
  isNonTruckingLiabilityActive,
} from "@shared/coverage-rules";
import { formatDollar } from "./formatters";

// CSR-792 — shape returned by `buildPolicyCoverageGroups` and consumed by
// the AI dialog (`AdditionalInsuredDialog.tsx`) and the LP dialog
// (`LossPayeeDialog.tsx`). The composite `<policyId>:<coverageKey>` id is
// what gets persisted; the `name` is the human label rendered next to the
// checkbox.
export interface PolicyCoverageGroup {
  policyId: string;
  policyLabel: string;
  coverages: Array<{ id: string; name: string }>;
}

// CSR-792 — promoted from `AdditionalInsuredDialog.tsx`. Builds policy +
// coverage groups for the multi-select pickers.
//
// `eligibleStatuses` filters input policies — when omitted, the legacy AI
// behavior (drop only "cancelled") is used so the AI dialog stays
// unchanged. The LP dialog passes `COI_ELIGIBLE_STATUSES` (per spec) so the
// picker only shows policies that can carry a COI today.
export function buildPolicyCoverageGroups(
  policies: Policy[],
  options?: { eligibleStatuses?: Set<string> },
): PolicyCoverageGroup[] {
  const filterFn = options?.eligibleStatuses
    ? (p: Policy) => options.eligibleStatuses!.has((p.status || "").toLowerCase())
    : (p: Policy) => (p.status || "").toLowerCase() !== "cancelled";

  return policies
    .filter(filterFn)
    .map((p) => {
      const lines = onPolicyCoverageLines(
        buildCoverageLines(p.coverages as PolicyCoverages | null),
      );
      return {
        policyId: p.id,
        policyLabel: `${p.policyNumber} · ${(p.type || "Policy").toUpperCase()}`,
        coverages: lines.map((l) => ({ id: `${p.id}:${l.key}`, name: l.name })),
      };
    })
    .filter((g) => g.coverages.length > 0);
}

export interface CoverageLineDetail {
  label: string;
  value: string;
}

export interface CoverageMetric {
  label: string;
  value: string;
}

export interface CoverageLine {
  key: string;
  name: string;
  status: CoverageStatus;
  limit: string;
  deductible: string;
  premium: string;
  metrics?: CoverageMetric[];
  premiumOnRight?: boolean;
  details?: CoverageLineDetail[];
  hasVehicleSection?: boolean;
}

// CSR-2148 — `buildCoverageLines` deliberately returns carrier-dropped lines so
// the Coverages tab can render them greyed as "Removed" (CSR-679/CSR-1910), the
// same way the Assets tab keeps showing removed vehicles. Everywhere else, a
// removed line is not a coverage on this policy: it must not be counted, and it
// must not be offered as something to attach an Additional Insured, Loss Payee,
// or Waiver of Subrogation to — the COI generator already refuses to render it
// (`isCoverageActive`, server/services/coiTemplate.ts). One predicate, so the
// tab and its count can never disagree about what is on the policy.
export function onPolicyCoverageLines(lines: CoverageLine[]): CoverageLine[] {
  return lines.filter((line) => line.status !== "removed");
}

// CSR-2148 — an Additional Insured / Loss Payee / Waiver saved before the
// carrier dropped a coverage still names it. That id is no longer offered by
// the picker but stays in the payload, because a later sync can reactivate the
// coverage (the Coverages tab remove dialog promises exactly that). So the
// "n of m selected" counter has to measure the selection against what is on
// offer — counting the raw selection reads "2 of 1 selected".
export function countOfferedSelections(selected: string[], offered: string[]): number {
  const offeredIds = new Set(offered);
  return selected.filter((id) => offeredIds.has(id)).length;
}

// CSR-2148 — the picker's "Select all" / "Deselect all" acts on what it shows,
// so it must leave the coverages it does not show alone. Replacing the whole
// selection would delete a link to a removed coverage that the user never saw
// and cannot restore, undoing the retention `countOfferedSelections` exists to
// support.
export function toggleOfferedSelection(selected: string[], offered: string[]): string[] {
  const offeredIds = new Set(offered);
  const retained = selected.filter((id) => !offeredIds.has(id));
  return countOfferedSelections(selected, offered) === offered.length
    ? retained
    : [...offered, ...retained];
}

// CSR-679: legacy AMS payloads (synced before the DC sibling shipped) arrive
// without a `status`. Treat that as `active` and let the empty-row gate below
// hide truly bogus rows.
function resolveStatus(block: { status?: CoverageStatus } | undefined): CoverageStatus {
  return block?.status ?? "active";
}

// Coverage fields are declared as `string` in `shared/schema.ts`, but some
// sync payloads arrive with numeric values. Widen these helpers so the
// formatter never crashes the policy detail render on a malformed shape.
export function formatDollarOrDash(value?: string | number | null): string {
  if (value == null || value === "") return "—";
  return formatDollar(value);
}

function formatBooleanOrDash(value?: boolean | null): string {
  if (value == null) return "—";
  return value ? "Yes" : "No";
}

function formatBasicPip(value?: string | number | null): string {
  if (value == null || value === "") return "—";
  if (value === "0" || value === 0) return "Included (no deductible)";
  return formatDollar(value);
}

function formatUmpdOrDash(value?: string | number | null): string {
  if (value == null || value === "") return "—";
  if (value === "included") return "Included";
  return formatDollar(value);
}

// Treat empty strings, nullish, and "0" (the DC sentinel for "absent" — Progressive
// often emits "0" when a coverage line is declined) as no value. Used to hide
// coverage rows whose every numeric field collapses to absent.
function hasValue(v?: string | null): boolean {
  if (v == null) return false;
  const s = String(v).trim();
  if (!s) return false;
  return !/^0+(\.0+)?$/.test(s);
}

function hasAnyValue(...vals: Array<string | null | undefined>): boolean {
  return vals.some(hasValue);
}

const AUTO_TYPE_LABELS: Record<string, string> = {
  any: "Any Auto",
  all_owned: "All Owned",
  scheduled: "Scheduled",
  hired: "Hired",
  non_owned: "Non-Owned",
};

export function buildCoverageLines(cov: PolicyCoverages | null | undefined): CoverageLine[] {
  if (!cov) return [];
  const out: CoverageLine[] = [];

  // CSR-1128: NTL and Auto Liability are mutually exclusive on a policy. When
  // NTL is active, suppress the (empty) Auto Liability card so the coverages
  // tab shows the NTL card + other coverages only. CSR-2118: "empty" means no
  // standard AL limit — Progressive's NTL slice carries UM / PIP values under
  // the AL block. An AL block WITH a standard limit is a real Auto Liability
  // line (e.g. one a CSR added to convert an NTL policy) and always renders;
  // hiding it made the add look like it never saved.
  const ntlActive = isNonTruckingLiabilityActive(cov);

  if (
    cov.generalLiability &&
    hasAnyValue(
      cov.generalLiability.eachOccurrence,
      cov.generalLiability.generalAggregate,
      cov.generalLiability.productsCompOpAgg,
      cov.generalLiability.personalAdvInjury,
      cov.generalLiability.damageToRented,
      cov.generalLiability.medExp,
    )
  ) {
    const gl = cov.generalLiability;
    const glPremium = (gl as { premium?: string | null }).premium;
    out.push({
      key: "generalLiability",
      name: "General Liability",
      status: resolveStatus(gl),
      limit: formatDollarOrDash(gl.generalAggregate),
      deductible: formatDollarOrDash(gl.eachOccurrence),
      premium: formatDollarOrDash(glPremium),
      metrics: [
        { label: "Aggregate Limit", value: formatDollarOrDash(gl.generalAggregate) },
        { label: "Each Occurrence", value: formatDollarOrDash(gl.eachOccurrence) },
      ],
      premiumOnRight: true,
      details: [
        { label: "Each Occurrence", value: formatDollarOrDash(gl.eachOccurrence) },
        { label: "Damage to Rented Premises", value: formatDollarOrDash(gl.damageToRented) },
        { label: "Med Exp (Any one person)", value: formatDollarOrDash(gl.medExp) },
        { label: "Personal & Adv Injury", value: formatDollarOrDash(gl.personalAdvInjury) },
        { label: "General Aggregate", value: formatDollarOrDash(gl.generalAggregate) },
        { label: "Products-Comp/Op Agg", value: formatDollarOrDash(gl.productsCompOpAgg) },
        { label: "Policy Type", value: gl.claimsMade ? "Claims Made" : "Occurrence" },
        {
          label: "Aggregate Applies Per",
          value:
            gl.aggregateAppliesPer === "loc"
              ? "Location"
              : gl.aggregateAppliesPer === "project"
                ? "Project"
                : "Policy",
        },
      ],
    });
  }

  if (
    cov.autoLiability &&
    (!ntlActive || hasStandardAutoLiabilityLimit(cov.autoLiability)) &&
    hasAnyValue(
      cov.autoLiability.combinedSingleLimit,
      cov.autoLiability.bodilyInjuryPerPerson,
      cov.autoLiability.bodilyInjuryPerAccident,
      cov.autoLiability.propertyDamage,
      cov.autoLiability.uninsuredMotoristCsl,
      cov.autoLiability.underinsuredMotoristBi,
      cov.autoLiability.medicalPayments,
      cov.autoLiability.basicPip,
      cov.autoLiability.pip,
    )
  ) {
    const al = cov.autoLiability;
    // CSR-1248: for split-limit policies (no CSL), show BI/BI/PD in the limit
    // column rather than "—". CSL takes priority when present.
    const alLimit: string = (() => {
      if (hasValue(al.combinedSingleLimit)) return formatDollarOrDash(al.combinedSingleLimit);
      const biPerson = al.bodilyInjuryPerPerson;
      const biAccident = al.bodilyInjuryPerAccident;
      const pd = al.propertyDamage;
      if (hasAnyValue(biPerson, biAccident, pd)) {
        return `${formatDollarOrDash(biPerson)}/${formatDollarOrDash(biAccident)}/${formatDollarOrDash(pd)}`;
      }
      return "—";
    })();
    out.push({
      key: "autoLiability",
      name: "Auto Liability",
      status: resolveStatus(al),
      limit: alLimit,
      deductible: "—",
      premium: "—",
      details: [
        { label: "Combined Single Limit", value: formatDollarOrDash(al.combinedSingleLimit) },
        { label: "Bodily Injury (Per Person)", value: formatDollarOrDash(al.bodilyInjuryPerPerson) },
        { label: "Bodily Injury (Per Accident)", value: formatDollarOrDash(al.bodilyInjuryPerAccident) },
        { label: "Property Damage", value: formatDollarOrDash(al.propertyDamage) },
        { label: "Auto Type", value: AUTO_TYPE_LABELS[al.autoType] || al.autoType || "—" },
        { label: "Uninsured Motorist", value: formatUmpdOrDash(al.uninsuredMotoristCsl) },
        { label: "UMPD", value: formatUmpdOrDash(al.uninsuredMotoristPd) },
        { label: "Underinsured Motorist", value: formatUmpdOrDash(al.underinsuredMotoristBi) },
        { label: "UIM Type", value: al.underinsuredMotoristType || "—" },
        { label: "Medical Payments", value: formatDollarOrDash(al.medicalPayments) },
        { label: "Basic PIP", value: formatBasicPip(al.basicPip) },
        { label: "PIP", value: formatDollarOrDash(al.pip) },
        { label: "UM Stacking", value: formatBooleanOrDash(al.umStacking) },
        { label: "UIM Stacking", value: formatBooleanOrDash(al.uimStacking) },
        { label: "UMPD Stacked", value: formatBooleanOrDash(al.umpdStacked) },
      ],
    });
  }

  if (cov.nonTruckingLiability && hasValue(cov.nonTruckingLiability.limit)) {
    out.push({
      key: "nonTruckingLiability",
      name: "Non-Trucking Liability",
      status: resolveStatus(cov.nonTruckingLiability),
      limit: formatDollarOrDash(cov.nonTruckingLiability.limit),
      deductible: "—",
      premium: "—",
    });
  }

  if (cov.hiredAutoLiability && hasValue(cov.hiredAutoLiability.combinedSingleLimit)) {
    out.push({
      key: "hiredAutoLiability",
      name: "Hired Auto Liability",
      status: resolveStatus(cov.hiredAutoLiability),
      limit: formatDollarOrDash(cov.hiredAutoLiability.combinedSingleLimit),
      deductible: "—",
      premium: "—",
    });
  }

  if (cov.hiredNonOwnedLiability && hasValue(cov.hiredNonOwnedLiability.limit)) {
    out.push({
      key: "hiredNonOwnedLiability",
      name: "Hired & Non-Owned Auto Liability",
      status: resolveStatus(cov.hiredNonOwnedLiability),
      limit: formatDollarOrDash(cov.hiredNonOwnedLiability.limit),
      deductible: "—",
      premium: "—",
    });
  }

  if (
    cov.umbrella &&
    hasAnyValue(cov.umbrella.eachOccurrence, cov.umbrella.aggregate, cov.umbrella.deductible)
  ) {
    const u = cov.umbrella;
    out.push({
      key: "umbrella",
      name: "Umbrella / Excess Liability",
      status: resolveStatus(u),
      limit: formatDollarOrDash(u.eachOccurrence),
      deductible: formatDollarOrDash(u.deductible),
      premium: "—",
      details: [
        { label: "Each Occurrence", value: formatDollarOrDash(u.eachOccurrence) },
        { label: "Aggregate", value: formatDollarOrDash(u.aggregate) },
        { label: "Type", value: u.isExcess ? "Excess" : "Umbrella" },
        { label: "Deductible", value: formatDollarOrDash(u.deductible) },
      ],
    });
  }

  if (
    cov.workersComp &&
    hasAnyValue(
      cov.workersComp.elEachAccident,
      cov.workersComp.elDiseaseEaEmployee,
      cov.workersComp.elDiseasePolicyLimit,
    )
  ) {
    const wc = cov.workersComp;
    out.push({
      key: "workersComp",
      name: "Workers Compensation",
      status: resolveStatus(wc),
      limit: formatDollarOrDash(wc.elEachAccident),
      deductible: "—",
      premium: "—",
      details: [
        { label: "Per Statute", value: wc.perStatute ? "Yes" : "No" },
        { label: "E.L. Each Accident", value: formatDollarOrDash(wc.elEachAccident) },
        { label: "E.L. Disease - Ea Employee", value: formatDollarOrDash(wc.elDiseaseEaEmployee) },
        { label: "E.L. Disease - Policy Limit", value: formatDollarOrDash(wc.elDiseasePolicyLimit) },
      ],
    });
  }

  if (cov.cargo && hasAnyValue(cov.cargo.limit, cov.cargo.deductible)) {
    out.push({
      key: "cargo",
      name: "Motor Truck Cargo",
      status: resolveStatus(cov.cargo),
      limit: formatDollarOrDash(cov.cargo.limit),
      deductible: formatDollarOrDash(cov.cargo.deductible),
      premium: "—",
    });
  }

  if (cov.onHook && hasAnyValue(cov.onHook.limit, cov.onHook.deductible)) {
    out.push({
      key: "onHook",
      name: "On-Hook",
      status: resolveStatus(cov.onHook),
      limit: formatDollarOrDash(cov.onHook.limit),
      deductible: formatDollarOrDash(cov.onHook.deductible),
      premium: "—",
    });
  }

  if (
    cov.physicalDamage &&
    hasAnyValue(
      cov.physicalDamage.comprehensiveDeductible,
      cov.physicalDamage.collisionDeductible,
    )
  ) {
    out.push({
      key: "physicalDamage",
      name: "Physical Damage",
      status: resolveStatus(cov.physicalDamage),
      limit: "—",
      deductible: `${formatDollarOrDash(cov.physicalDamage.comprehensiveDeductible)} / ${formatDollarOrDash(
        cov.physicalDamage.collisionDeductible,
      )}`,
      premium: "—",
      details: [
        { label: "Comprehensive Deductible", value: formatDollarOrDash(cov.physicalDamage.comprehensiveDeductible) },
        { label: "Collision Deductible", value: formatDollarOrDash(cov.physicalDamage.collisionDeductible) },
      ],
      hasVehicleSection: true,
    });
  }

  if (
    cov.trailerInterchange &&
    hasAnyValue(cov.trailerInterchange.limit, cov.trailerInterchange.deductible)
  ) {
    out.push({
      key: "trailerInterchange",
      name: "Trailer Interchange",
      status: resolveStatus(cov.trailerInterchange),
      limit: formatDollarOrDash(cov.trailerInterchange.limit),
      deductible: formatDollarOrDash(cov.trailerInterchange.deductible),
      premium: "—",
    });
  }

  if (
    cov.nonOwnedTrailerPhysicalDamage &&
    hasAnyValue(
      cov.nonOwnedTrailerPhysicalDamage.limit,
      cov.nonOwnedTrailerPhysicalDamage.deductible,
    )
  ) {
    out.push({
      key: "nonOwnedTrailerPhysicalDamage",
      name: "Non-Owned Trailer Physical Damage",
      status: resolveStatus(cov.nonOwnedTrailerPhysicalDamage),
      limit: formatDollarOrDash(cov.nonOwnedTrailerPhysicalDamage.limit),
      deductible: formatDollarOrDash(cov.nonOwnedTrailerPhysicalDamage.deductible),
      premium: "—",
    });
  }

  if (cov.rentalReimbursement && hasAnyValue(cov.rentalReimbursement.perDay, cov.rentalReimbursement.maxPayout)) {
    const rr = cov.rentalReimbursement;
    out.push({
      key: "rentalReimbursement",
      name: "Rental Reimbursement",
      status: resolveStatus(rr),
      limit: formatDollarOrDash(rr.maxPayout),
      deductible: "—",
      premium: "—",
      details: [
        { label: "Per Day", value: formatDollarOrDash(rr.perDay) },
        { label: "Max Payout", value: formatDollarOrDash(rr.maxPayout) },
        { label: "Applies To", value: rr.appliesTo || "—" },
      ],
    });
  }

  if (cov.nonOwnedAuto && hasAnyValue(cov.nonOwnedAuto.limit)) {
    out.push({
      key: "nonOwnedAuto",
      name: "Non-Owned Auto",
      status: resolveStatus(cov.nonOwnedAuto),
      limit: formatDollarOrDash(cov.nonOwnedAuto.limit),
      deductible: "—",
      premium: "—",
    });
  }

  return out;
}
